import React, { useEffect, useState } from "react";
import { fetchWallet, redeemWalletCode } from "../services/accountService";
import "../styles/pages/AccountPageSpacing.css";
import "../styles/pages/WalletPage.css";
import { WalletSummary } from "../types/store";
import { formatCurrency } from "../utils/currency";
import { useAuth } from "../contexts/AuthContext";
import { MobilePageHeader } from "../components/layout/MobileAppChrome";
import "../styles/pages/MobileMarketplace.css";
import walletLaptopIllustration from "../assets/wallet-laptop.png";

const WalletPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [wallet, setWallet] = useState<WalletSummary | null>(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [showCodeEntry, setShowCodeEntry] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  useEffect(() => {
    const loadWallet = async () => {
      if (!isAuthenticated) {
        setWallet(null);
        setFeedback(null);
        setLoading(false);
        return;
      }

      try {
        setWallet(await fetchWallet());
        setFeedback(null);
      } catch {
        setFeedback({ type: "error", message: "Unable to load wallet right now." });
      } finally {
        setLoading(false);
      }
    };

    void loadWallet();
  }, [isAuthenticated]);

  const handleRedeem = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!code.trim()) {
      setFeedback({ type: "error", message: "Enter your code to add money to wallet." });
      return;
    }

    setRedeeming(true);
    try {
      const response = await redeemWalletCode(code.trim());
      setWallet(response);
      setCode("");
      setShowCodeEntry(false);
      setFeedback({ type: "success", message: "Wallet updated successfully." });
    } catch {
      setFeedback({ type: "error", message: "Unable to redeem that code." });
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <section className="shell section page-section wallet-page">
      <MobilePageHeader title="My Wallet" />
      <div className="page-header">
        <span className="eyebrow">Wallet</span>
        <h1>Your wallet and reward balance</h1>
        <p>Use admin-issued wallet codes here and track cashback credits from completed orders.</p>
      </div>

      <div className="wallet-layout">
        <article className="store-card wallet-card">
          <div className="wallet-card__hero">
            <div className="wallet-card__copy">
              <h2>Available balance</h2>
              <div className="checkout-delivery-mode wallet-card__balance">
                <span>Ready to use on orders</span>
                <strong>{wallet ? formatCurrency(wallet.balance) : loading ? "Loading..." : formatCurrency(0)}</strong>
              </div>
            </div>
            <img className="wallet-card__image" src={walletLaptopIllustration} alt="Wallet illustration" />
          </div>
          <form className="wallet-card__redeem" onSubmit={handleRedeem}>
            <label>
              Enter admin wallet code
              <input
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="Example: ADD500"
                disabled={redeeming}
              />
            </label>
            <button className="button" type="submit" disabled={redeeming} aria-busy={redeeming}>
              {redeeming ? "Applying..." : "Add"}
            </button>
            {feedback ? <p className={`wallet-feedback wallet-feedback--${feedback.type}`}>{feedback.message}</p> : null}
          </form>
        </article>

        <aside className="store-card wallet-transactions">
          <div className="wallet-transactions__header">
            <div>
              <span className="eyebrow">Activity</span>
              <h2>All transactions</h2>
            </div>
            <strong>{wallet?.transactions.length || 0}</strong>
          </div>
          {wallet?.transactions.length ? (
            <div className="wallet-transactions__list">
              {wallet.transactions.map((transaction) => {
                const isCredit = transaction.type !== "DEBIT";

                return (
                  <article className="wallet-transaction" key={transaction.id}>
                    <div>
                      <strong>{transaction.description}</strong>
                      <time>
                        {new Date(transaction.createdAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </time>
                    </div>
                    <span className={isCredit ? "is-credit" : "is-debit"}>
                      {isCredit ? "+" : "-"}{formatCurrency(Math.abs(transaction.amount))}
                    </span>
                  </article>
                );
              })}
            </div>
          ) : (
            <p>No wallet activity yet.</p>
          )}
        </aside>
      </div>
      <div className="wallet-mobile-view">
        <article className="wallet-mobile-balance">
          <span>Wallet Balance</span>
          <strong>{wallet ? formatCurrency(wallet.balance) : loading ? "Loading..." : formatCurrency(0)}</strong>
          <small>Available Balance</small>
          {showCodeEntry ? (
            <form onSubmit={handleRedeem}>
              <input value={code} onChange={(event) => setCode(event.target.value)} placeholder="Enter wallet code" autoFocus />
              <button disabled={redeeming}>{redeeming ? "Applying..." : "Apply"}</button>
            </form>
          ) : (
            <button className="wallet-mobile-balance__add" type="button" onClick={() => setShowCodeEntry(true)}>Add Money</button>
          )}
          {feedback ? <p className={`wallet-feedback wallet-feedback--${feedback.type}`}>{feedback.message}</p> : null}
        </article>
        <section className="wallet-mobile-transactions">
          <h2>Wallet Transactions</h2>
          {wallet?.transactions.length ? wallet.transactions.map((transaction) => (
            <article key={transaction.id}>
              <span>
                <strong className={transaction.type !== "DEBIT" ? "is-credit" : "is-debit"}>
                  {transaction.type !== "DEBIT" ? "+" : "-"}{formatCurrency(Math.abs(transaction.amount))}
                </strong>
                <small>{transaction.description}</small>
              </span>
              <time>{new Date(transaction.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</time>
            </article>
          )) : <p>No wallet activity yet.</p>}
          <button type="button" className="wallet-mobile-transactions__view-all">View All Transactions</button>
        </section>
      </div>
    </section>
  );
};

export default WalletPage;
